// 設備「錄影與儲存」分頁的每路錄影健康（SPEC #vms-web-rec-health；device/nvr#219 第 3 項）：
//   - 通道：最上面一區「錄影健康」——狀態（正常錄影中 / 現在不用錄 / 錄影中斷：原因 / 沒有回報）、最後寫入、丟掉的影格、存檔耗時、寫入失敗
//   - 主機（NVR / Kinio Station）：儲存摘要下面「每路錄影健康」表，中斷的排最前面
// 讀通道子 Thing（<主機>:chN）自己的 shadow（RecordingPolicy 讀同一份、同一個快取鍵），每 30 秒。
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getShadow } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';
import { durationText } from '../../lib/duration';
import { nearStamp, stamp } from '../../lib/timefmt';
import { useTzResolver } from '../../lib/useTz';
import { reasonKey, recHealthOf, recHealthRank, type RecHealth } from './recHealthData';

const POLL_MS = 30_000;

function ownShadow(d: VmsDevice) {
  return {
    queryKey: ['shadow-own', d.homeId, d.aid],
    queryFn: () => getShadow(d.apiEndpoint, d.homeId, d.aid),
    refetchInterval: POLL_MS,
    retry: false,
  };
}

/** 狀態那一句（含顏色）；h = null → 沒有回報；unreadable → 讀不到。 */
function StateLine({ h, unreadable, tz, now }: { h: RecHealth | null; unreadable?: boolean; tz?: string; now: number }) {
  const { t } = useTranslation();
  if (unreadable)
    return (
      <span className="muted" data-rec-state="unreadable">
        {t('rech.state_unreadable')}
      </span>
    );
  if (!h)
    return (
      <span data-rec-state="none">
        <span className="muted">{t('rech.state_none')}</span>
        <div className="muted small">{t('rech.none_note')}</div>
      </span>
    );
  if (h.state === 'ok')
    return (
      <span className="ok-text" data-rec-state="ok">
        {t('rech.state_ok')}
      </span>
    );
  if (h.state === 'idle')
    return (
      <span data-rec-state="idle">
        <span className="muted">{t('rech.state_idle')}</span>
        <div className="muted small">{t('rech.idle_note')}</div>
      </span>
    );
  return (
    <span data-rec-state="interrupted">
      <strong className="error">{t('rech.state_interrupted', { reason: t(reasonKey(h.reason)) })}</strong>
      {h.since !== undefined && (
        <div className="small" data-testid="rech-since">
          {t('rech.since', { at: nearStamp(h.since * 1000, now, tz), dur: sinceDur(t, now / 1000 - h.since) })}
        </div>
      )}
    </span>
  );
}

/** 中斷了多久：1 小時內寫到分鐘（「25 分鐘」），以上照全站的時長寫法（「2 小時 5 分」）。 */
function sinceDur(t: (k: string, o?: Record<string, unknown>) => string, sec: number): string {
  if (sec < 60) return t('rech.dur_lt1m');
  if (sec < 3600) return t('rech.dur_min', { count: Math.floor(sec / 60) });
  return durationText(t, sec);
}

function fsyncText(t: (k: string, o?: Record<string, unknown>) => string, h: RecHealth): string {
  if (h.fsyncP95 === undefined && h.fsyncMax === undefined) return t('rech.fsync_none');
  return t('rech.fsync_v', { p95: h.fsyncP95 ?? '—', max: h.fsyncMax ?? '—' });
}

/** 通道的「錄影健康」區。 */
export function ChannelRecHealth({ dev, host }: { dev: VmsDevice; host?: VmsDevice }) {
  const { t } = useTranslation();
  const tzOf = useTzResolver();
  const tz = tzOf(dev.homeId);
  const q = useQuery(ownShadow(dev));
  const now = Date.now();
  if (q.isLoading) return <p className="muted">{t('rech.loading')}</p>;
  const h = recHealthOf(q.data?.state?.reported as Record<string, unknown> | undefined);
  const offline = host?.presence === 'offline';
  return (
    <div className="card rech" data-testid="rech-channel">
      {offline && h && (
        <p className="notice small" data-testid="rech-host-offline">
          {t('rech.host_offline', { at: host?.lastSeen ? stamp(host.lastSeen, { tz }) : '—' })}
        </p>
      )}
      <table className="grid dinfo">
        <tbody>
          <tr>
            <th scope="row">{t('rech.col_state')}</th>
            <td>
              <StateLine h={h} unreadable={!!q.error} tz={tz} now={now} />
            </td>
          </tr>
          {h && h.lastWriteAt !== undefined && (
            <tr>
              <th scope="row">{t('rech.last_write')}</th>
              <td data-testid="rech-last-write">{nearStamp(h.lastWriteAt * 1000, now, tz) || stamp(h.lastWriteAt * 1000, { tz, seconds: true })}</td>
            </tr>
          )}
          {h && h.framesDropped1h !== undefined && (
            <tr>
              <th scope="row">{t('rech.dropped')}</th>
              <td data-testid="rech-dropped">
                <span className={h.framesDropped1h > 0 ? 'warn-text' : undefined}>{t('rech.dropped_n', { n: h.framesDropped1h })}</span>
                <div className="muted small">{t('rech.dropped_note')}</div>
              </td>
            </tr>
          )}
          {h && (
            <tr>
              <th scope="row">{t('rech.fsync')}</th>
              <td data-testid="rech-fsync">{fsyncText(t, h)}</td>
            </tr>
          )}
          {h && h.writeErrors24h !== undefined && (
            <tr>
              <th scope="row">{t('rech.write_err')}</th>
              <td data-testid="rech-write-err" className={h.writeErrors24h > 0 ? 'error' : undefined}>
                {t('rech.write_err_n', { n: h.writeErrors24h })}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {h && <p className="muted small">{t('rech.reset_note')}</p>}
    </div>
  );
}

/** 主機的「每路錄影健康」表（一路一列，中斷的在前）。 */
export function HostRecHealth({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const tzOf = useTzResolver();
  const tz = tzOf(dev.homeId);
  const chans = dev.children.filter((c) => c.isCamera);
  const qs = useQueries({ queries: chans.map(ownShadow) });
  if (!chans.length) return null;
  const now = Date.now();
  const rows = chans
    .map((c, i) => ({ c, i, q: qs[i], h: recHealthOf(qs[i]?.data?.state?.reported as Record<string, unknown> | undefined) }))
    .sort((a, b) => recHealthRank(a.h) - recHealthRank(b.h) || a.i - b.i);
  return (
    <section className="ddev-section" data-ddev-section="rec-health" data-testid="rech-host">
      <h3 className="section-title">{t('rech.table_title')}</h3>
      {dev.presence === 'offline' && (
        <p className="notice small">{t('rech.host_offline', { at: dev.lastSeen ? stamp(dev.lastSeen, { tz }) : '—' })}</p>
      )}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="grid rech-table">
          <thead>
            <tr>
              <th scope="col">{t('rech.col_channel')}</th>
              <th scope="col">{t('rech.col_state')}</th>
              <th scope="col">{t('rech.dropped')}</th>
              <th scope="col">{t('rech.fsync')}</th>
              <th scope="col">{t('rech.write_err')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ c, q, h }) => (
              <tr key={c.key} data-rech-row={c.aid}>
                <td>
                  <Link to={`/devices/${encodeURIComponent(c.homeId)}/${encodeURIComponent(c.aid)}?tab=recording`}>{c.name}</Link>
                </td>
                <td>{q?.isLoading ? <span className="muted">{t('rech.loading')}</span> : <StateLine h={h} unreadable={!!q?.error} tz={tz} now={now} />}</td>
                <td className={h?.framesDropped1h ? 'warn-text' : undefined}>{h?.framesDropped1h !== undefined ? t('rech.dropped_n', { n: h.framesDropped1h }) : ''}</td>
                <td>{h ? fsyncText(t, h) : ''}</td>
                <td className={h?.writeErrors24h ? 'error' : undefined}>{h?.writeErrors24h !== undefined ? t('rech.write_err_n', { n: h.writeErrors24h }) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">{t('rech.reset_note')}</p>
    </section>
  );
}
